/* placement.js — cabinet positioning geometry.
 * Ported from lhc-app.html: getWallPlacementInfo (~31343), lhcRoomNormal (~31406),
 * cabinetWorldPos (~31510), _placementAABB (~31783), lhcVSpan (~32999),
 * kitchenBackZ (~31474) + the no-wall back-wall row fallback (getCabinetStartX
 * ~31124 / computeTotalWidth ~31156 / buildSofa row layout ~32856 — plan S-15 /
 * WI-13) so wall-less legacy designs (sofa-era saves, clrLeft/clrRight, no wallId)
 * still position. Units = METRES.
 *
 * Pure: all inputs passed explicitly (placement, def, walls). cabinetWorldPos accepts
 * OPTIONAL trailing (placements, lib) — when provided, a non-island placement without
 * a wall resolves through the sequential back-wall row instead of returning null.
 */
import { WALL_THICKNESS } from "./constants";
import { roleBaseY } from "./rules";
import { sidePanelExtents } from "./sidePanelClearance";
import { effDef } from "./pricing";
import { getWallGraph, jointTol, jointsCoincide } from "./wallGraph";
// Function-level-only cycle (placement → wallJoins → walls → snapping → placement) —
// safe: exports are only referenced inside function bodies, never at module eval.
import { effectiveThickness } from "./wallJoins";

export function getWallPlacementInfo(w, walls) {
  if (!w || !Array.isArray(walls) || walls.length === 0) return null;
  const dx = w.end[0] - w.start[0];
  const dz = w.end[1] - w.start[1];
  const len = Math.hypot(dx, dz);
  if (len < 0.01) return null;
  const dirX = dx / len, dirZ = dz / len;
  let nx = -dirZ, nz = dirX;
  const cgX = walls.reduce((s, ww) => s + (ww.start[0] + ww.end[0]) / 2, 0) / walls.length;
  const cgZ = walls.reduce((s, ww) => s + (ww.start[1] + ww.end[1]) / 2, 0) / walls.length;
  const wallMidX = (w.start[0] + w.end[0]) / 2;
  const wallMidZ = (w.start[1] + w.end[1]) / 2;
  if (nx * (cgX - wallMidX) + nz * (cgZ - wallMidZ) < 0) { nx = -nx; nz = -nz; }
  // Per-wall thickness (Phase-1 fix): the inner face of a wall with a thickness
  // override is at ITS half-thickness, not the global default's.
  const _ht = effectiveThickness(w) / 2;
  const innerFaceX = wallMidX + nx * _ht;
  const innerFaceZ = wallMidZ + nz * _ht;
  const startAlongWall = (w.start[0] - wallMidX) * dirX + (w.start[1] - wallMidZ) * dirZ;
  return { w, len, dirX, dirZ, nx, nz, innerFaceX, innerFaceZ, wallMidX, wallMidZ, startAlongWall };
}

/** lhcRoomNormal(wall, walls): inward (room-side) normal. (~31406)
 *
 *  v0.53 GRAPH-BACKED: the answer now comes from room-FACE ownership in the wall
 *  topology graph (wallGraph.js) — which side of this wall actually borders a
 *  closed room. This is exact in L / U / staircase rooms and in rooms with
 *  irregular (few-cm open) corners, where the old implementation — a boundary
 *  chain-walk with a PRIVATE 3 mm tolerance — failed to close the loop and fell
 *  back to the centroid-of-wall-midpoints heuristic, which points the WRONG way
 *  for walls near a notch (BUG-B family). The 3 mm `same()` predicate and the
 *  ~70-line walk are gone; connectivity has one authority now.
 *
 *  Contract preserved:
 *   - partitions/peninsulas → neutral left-perp (double-sided walls, callers
 *     resolve the side via faceSign);
 *   - no closed room on either side (open plans mid-draw) → centroid normal
 *     (getWallPlacementInfo), exactly the old fallback;
 *   - degenerate wall → null. */
export function lhcRoomNormal(wall, walls) {
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return null;
  const _tStyle = wall.style || "standard";
  if (_tStyle === "partition" || _tStyle === "peninsula") return { nx: -info.dirZ, nz: info.dirX };
  const wi = getWallGraph(walls).walls.get(wall.id);
  if (wi && wi.interiorNormal) return { nx: wi.interiorNormal[0], nz: wi.interiorNormal[1] };
  // OPEN-CHAIN inference (user mandate: NO centroid heuristic for open chains).
  // The interior side comes from the chain's WINDING: orient the connected walls
  // head-to-tail and accumulate the signed turning — every wall in the chain then
  // shares ONE consistent interior side, exactly like the closed-room answer the
  // same chain will produce once it closes.
  const cw = chainWindingNormal(wall, walls);
  if (cw) return cw;
  return { nx: info.nx, nz: info.nz }; // isolated / perfectly straight chain → centroid fallback
}

/** chainWindingNormal(wall, walls): consistent interior normal for a wall in an
 *  OPEN chain, derived from topology instead of a centroid guess.
 *  1. BFS from the host wall across shared joints, orienting every connected
 *     straight wall head-to-tail (one travel direction per wall).
 *  2. Sum the signed turn angle at each degree-2 joint of the oriented chain.
 *  3. Total turn > 0 (net LEFT turning) ⇒ the interior is on the LEFT of travel
 *     for EVERY wall in the chain; < 0 ⇒ right — the standard polygon-winding
 *     rule, applied before the polygon exists.
 *  Null when the chain has < 2 walls or no turn (no signal — caller falls back). */
function chainWindingNormal(wall, walls) {
  if (!wall || wall.type === "arc") return null;
  const byId = new Map();
  (walls || []).forEach((w) => {
    if (w && w.type !== "arc" && (w.style || "standard") === "standard") byId.set(w.id, w);
  });
  if (!byId.has(wall.id) || byId.size < 2) return null;
  const nodes = getWallGraph(walls).nodes || [];
  const orient = new Map([[wall.id, 1]]); // +1 = start→end travel, −1 = reversed
  const queue = [wall.id];
  while (queue.length) {
    const id = queue.shift();
    const w = byId.get(id);
    const o = orient.get(id);
    nodes.forEach((n) => {
      if (!n.ends.some((e) => e.wallId === id)) return;
      const wArrivesHere = jointsCoincide(o > 0 ? w.end : w.start, n.p);
      n.ends.forEach((e) => {
        if (e.wallId === id || !byId.has(e.wallId) || orient.has(e.wallId)) return;
        const w2 = byId.get(e.wallId);
        const w2StartsHere = jointsCoincide(w2.start, n.p);
        // continue the travel: leave the node if we arrived, arrive if we leave
        orient.set(e.wallId, wArrivesHere ? (w2StartsHere ? 1 : -1) : (w2StartsHere ? -1 : 1));
        queue.push(e.wallId);
      });
    });
  }
  if (orient.size < 2) return null;
  const dirOf = (w, o) => {
    const L = Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]) || 1;
    return [(o * (w.end[0] - w.start[0])) / L, (o * (w.end[1] - w.start[1])) / L];
  };
  let turn = 0;
  nodes.forEach((n) => {
    const here = n.ends.filter((e) => orient.has(e.wallId));
    if (here.length !== 2 || here[0].wallId === here[1].wallId) return;
    const wa = byId.get(here[0].wallId), wb = byId.get(here[1].wallId);
    const oa = orient.get(here[0].wallId), ob = orient.get(here[1].wallId);
    const aArrives = jointsCoincide(oa > 0 ? wa.end : wa.start, n.p);
    const [wIn, oIn, wOut, oOut] = aArrives ? [wa, oa, wb, ob] : [wb, ob, wa, oa];
    const dA = dirOf(wIn, oIn), dB = dirOf(wOut, oOut);
    turn += Math.atan2(dA[0] * dB[1] - dA[1] * dB[0], dA[0] * dB[0] + dA[1] * dB[1]);
  });
  let s = 0;
  if (Math.abs(turn) >= 1e-6) {
    s = turn > 0 ? 1 : -1; // net left turn ⇒ interior on the left of travel
  } else {
    // NET-ZERO TURN (2026-08-21 user report: a Z/step-shaped 3-wall chain reads
    // +90° then −90° — no net winding, so the side fell to the CENTROID guess
    // and the interior of the step's last wall flipped when a 4th wall was
    // added, jumping its interior-face dimension by exactly one thickness,
    // 2928 → 3078). The SIGNED AREA of the ordered chain (implied closure —
    // plain shoelace) still carries the winding a net turn cannot: it is what
    // the same chain will answer once it closes into a room. Sign convention
    // pinned against the turn rule (2A > 0 ⇔ net left ⇔ interior left).
    // Simple paths only — a branched chain has no polyline order (→ null).
    const headOf = (w, o2) => (o2 > 0 ? w.end : w.start);
    const tailOf = (w, o2) => (o2 > 0 ? w.start : w.end);
    const ids2 = [...orient.keys()];
    const starts = ids2.filter((id) =>
      !ids2.some((j) => j !== id && jointsCoincide(headOf(byId.get(j), orient.get(j)), tailOf(byId.get(id), orient.get(id)))));
    if (starts.length !== 1) return null;
    const verts = [];
    let cur = starts[0];
    const seen = new Set();
    while (cur != null && !seen.has(cur)) {
      seen.add(cur);
      const w = byId.get(cur), o2 = orient.get(cur);
      if (!verts.length) verts.push(tailOf(w, o2));
      verts.push(headOf(w, o2));
      const head = headOf(w, o2);
      cur = ids2.find((id) => !seen.has(id) && jointsCoincide(tailOf(byId.get(id), orient.get(id)), head));
    }
    if (seen.size !== ids2.length || verts.length < 3) return null; // branch / loop — no order
    let a2 = 0;
    for (let k = 0; k < verts.length; k++) {
      const p = verts[k], q = verts[(k + 1) % verts.length];
      a2 += p[0] * q[1] - q[0] * p[1];
    }
    if (Math.abs(a2) < 1e-9) return null; // degenerate — genuinely no signal
    s = a2 > 0 ? 1 : -1;
  }
  const o = orient.get(wall.id);
  const L = Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]) || 1;
  const d = [(o * (wall.end[0] - wall.start[0])) / L, (o * (wall.end[1] - wall.start[1])) / L];
  return { nx: -d[1] * s, nz: d[0] * s };
}

/** kitchenBackZ(walls): world Z of the back face of back-row cabinets — inner face of the
 *  lowest-z horizontal wall + 5mm gap, or -0.30 with no (horizontal) walls. (~31474) */
export function kitchenBackZ(walls) {
  if (!walls || walls.length === 0) return -0.30; // fallback: slight back-shift from origin
  let backZ = Infinity;
  walls.forEach((w) => {
    const isHorizontal = Math.abs(w.start[1] - w.end[1]) < 0.05;
    if (isHorizontal) {
      const mz = (w.start[1] + w.end[1]) / 2;
      if (mz < backZ) backZ = mz;
    }
  });
  if (backZ === Infinity) return -0.30;
  return backZ + WALL_THICKNESS / 2 + 0.005; // 5mm gap between cabinet and wall
}

/** computeTotalWidth (~31156): row width incl. per-placement clearances (wall-less entries
 *  only — wallId'd placements live on their wall, not in the legacy row). */
function rowTotalWidth(placements, lib) {
  return (placements || []).reduce((s, q) => {
    if (q.wallId || q.island) return s;
    const d = effDef(q, lib);
    return s + ((d && d.width) || 0) + (q.clrLeft || 0) + (q.clrRight || 0);
  }, 0);
}

/** getCabinetStartX (~31124) with the legacy fallback: back-wall inner-corner start when a
 *  back wall exists, else the centered free-standing row (-totalWidth/2). */
export function rowStartX(walls, placements, lib) {
  const ws = walls || [];
  let back = null, backZv = Infinity;
  ws.forEach((w) => {
    const isHorizontal = Math.abs(w.start[1] - w.end[1]) < 0.05;
    if (!isHorizontal) return;
    const mz = (w.start[1] + w.end[1]) / 2;
    if (mz < backZv) { backZv = mz; back = w; }
  });
  if (back) {
    const backMinX = Math.min(back.start[0], back.end[0]);
    const backZ = (back.start[1] + back.end[1]) / 2;
    const TOL = jointTol(); // v0.53: same VALUE as before (WT+0.05), single source now
    let startX = backMinX; // straight wall: cabinet starts at the wall face (point 0)
    ws.forEach((w) => {
      if (w === back) return;
      const isVertical = Math.abs(w.start[0] - w.end[0]) < 0.05;
      if (!isVertical) return;
      const wx = (w.start[0] + w.end[0]) / 2;
      if (Math.abs(wx - backMinX) < TOL) {
        const wMinZ = Math.min(w.start[1], w.end[1]);
        const wMaxZ = Math.max(w.start[1], w.end[1]);
        if (wMinZ <= backZ + TOL && wMaxZ >= backZ - TOL) {
          startX = wx + WALL_THICKNESS / 2; // L-shape: inner corner
        }
      }
    });
    return startX;
  }
  return -rowTotalWidth(placements, lib) / 2;
}

/** S-15 / WI-13 — the buildSofa sequential row layout (~32856) for ONE placement: center X
 *  of a wall-less placement in the legacy back-wall row (absLeft anchors from the row start;
 *  clrLeft/clrRight entries flow from the running cursor). */
export function fallbackRowCenterX(p, placements, lib, walls) {
  const startX = rowStartX(walls, placements, lib);
  const pd = effDef(p, lib) || {};
  if (p.absLeft != null) return startX + p.absLeft + (pd.width || 0) / 2;
  let cursorX = startX;
  for (const q of placements || []) {
    if (q.wallId || q.island) continue;
    const d = effDef(q, lib);
    if (!d) continue;
    if (q.absLeft != null) {
      if (q === p || q.id === p.id) return startX + q.absLeft + d.width / 2;
      cursorX = startX + q.absLeft + d.width;
      continue;
    }
    const clrL = q.clrLeft || 0, clrR = q.clrRight || 0;
    cursorX += clrL;
    const centerX = cursorX + d.width / 2;
    cursorX += d.width + clrR;
    if (q === p || q.id === p.id) return centerX;
  }
  // p is not (yet) in placements — a candidate appends at the row cursor
  return cursorX + (p.clrLeft || 0) + (pd.width || 0) / 2;
}

/** cabinetWorldPos(p, def, walls[, placements, lib]): world center + facing angle. (~31510)
 *  With the optional (placements, lib) pair, a non-island placement without a resolvable
 *  wall falls back to the legacy back-wall row (placeKitchenModule ~32634: cz = kitchenBackZ
 *  + depth/2, angle 0 facing +Z) instead of returning null — S-15 wall-less designs. */
export function cabinetWorldPos(p, def, walls, placements, lib) {
  if (!p || !def) return null;
  if (p.island) {
    // ISLAND ANGLE CONSISTENCY (2026-07-14 user report: "island isn't showing
    // right in 3D and Present"): plan renders from dirX/dirZ, but 3D/Present/
    // collision read `angle`. The old island angle came from the ALONG
    // direction (atan2(dirX,dirZ)) while wall cabinets derive it from the
    // FACING normal (atan2(nx,nz)) — islands rendered 90° rotated in 3D and
    // carried a swapped collision box. One convention now: angle = facing
    // normal, exactly like wall placements (P1 stores nx/nz; legacy islands
    // without them derive the left-perp of dir).
    const inx = p.nx !== undefined ? p.nx : -(p.dirZ === undefined ? 0 : p.dirZ);
    const inz = p.nz !== undefined ? p.nz : (p.dirX === undefined ? 1 : p.dirX);
    return {
      cx: p.x, cz: p.z,
      angle: Math.atan2(inx, inz),
      nx: inx, nz: inz,
      dirX: p.dirX || 1, dirZ: p.dirZ || 0, wall: null,
    };
  }
  const wall = p.wallId ? walls.find((w) => w.id === p.wallId) : null;
  if (!wall) {
    if (placements && lib) {
      const cx = fallbackRowCenterX(p, placements, lib, walls || []);
      const cz = kitchenBackZ(walls || []) + def.depth / 2;
      return { cx, cz, angle: 0, nx: 0, nz: 1, dirX: 1, dirZ: 0, wall: null };
    }
    return null;
  }
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return null;
  const absL = p.absLeft != null ? p.absLeft : 0;
  const alongX = wall.start[0] + info.dirX * (absL + def.width / 2);
  const alongZ = wall.start[1] + info.dirZ * (absL + def.width / 2);
  const _wStyle = wall.style || "standard";
  // Per-wall thickness: the cabinet back sits on THIS wall's face, not the default's.
  const _wt2 = _wStyle === "peninsula" ? 0 : effectiveThickness(wall) / 2;

  if (_wStyle === "partition" || _wStyle === "peninsula") {
    const _fs = p.faceSign === -1 ? -1 : 1;
    const fnx = -info.dirZ * _fs, fnz = info.dirX * _fs;
    const fcx = alongX + fnx * (_wt2 + def.depth / 2);
    const fcz = alongZ + fnz * (_wt2 + def.depth / 2);
    return { cx: fcx, cz: fcz, angle: Math.atan2(fnx, fnz), nx: fnx, nz: fnz, dirX: fnz, dirZ: -fnx, wall };
  }
  const rn = lhcRoomNormal(wall, walls);
  if (rn) {
    const cx = alongX + rn.nx * (_wt2 + def.depth / 2);
    const cz = alongZ + rn.nz * (_wt2 + def.depth / 2);
    return { cx, cz, angle: Math.atan2(rn.nx, rn.nz), nx: rn.nx, nz: rn.nz, dirX: rn.nz, dirZ: -rn.nx, wall };
  }
  const cx = alongX + info.nx * (_wt2 + def.depth / 2);
  const cz = alongZ + info.nz * (_wt2 + def.depth / 2);
  return { cx, cz, angle: Math.atan2(info.nx, info.nz), nx: info.nx, nz: info.nz, dirX: info.dirX, dirZ: info.dirZ, wall };
}

/** placementAABB(p, def, walls): world axis-aligned box. (~31783) */
export function placementAABB(p, def, walls) {
  if (!def) return null;
  const pos = cabinetWorldPos(p, def, walls);
  if (!pos) return null;
  const a = pos.angle || 0;
  const ca = Math.abs(Math.cos(a)), sa = Math.abs(Math.sin(a));
  /* SIDE PANELS ARE PHYSICAL (2026-09-07 user: "the side panel goes through the
   * wall or through the cabinets"): a decor end panel grows the box on its
   * side(s) and shifts the centre so the CARCASS stays exactly where it is.
   * Every caller — collision, the drag sweep, island magnets, validation —
   * inherits the real footprint from here. */
  const _spE = sidePanelExtents(p, def);
  const _spW = def.width + _spE.left + _spE.right;
  const _spS = (_spE.right - _spE.left) / 2;
  const cxp = pos.cx + Math.cos(a) * _spS, czp = pos.cz - Math.sin(a) * _spS;
  const halfX = ca * _spW / 2 + sa * def.depth / 2;
  const halfZ = sa * _spW / 2 + ca * def.depth / 2;
  const [minY0, maxY0] = lhcVSpan(p, def);
  return { minX: cxp - halfX, maxX: cxp + halfX, minZ: czp - halfZ, maxZ: czp + halfZ, minY: minY0 - _spE.bottom, maxY: maxY0 + _spE.top, cx: cxp, cz: czp, def };
}

/** placementOBB(p, def, walls): the ORIENTED footprint (batch 7, 2026-09-28) — the same
 *  pose maths as placementAABB (world centre incl. the side-panel shift, width axis u,
 *  depth axis v, half extents, vertical span) without the axis-aligned inflation: on a
 *  wall at any angle other than 0/90° the AABB of a 600 unit grows by up to 30% along
 *  X/Z, so two neighbours that merely TOUCHED read as overlapping ("says collision even
 *  though it fits"). Collision, validation and the contact sweep read this box. */
export function placementOBB(p, def, walls) {
  if (!def) return null;
  const pos = cabinetWorldPos(p, def, walls);
  if (!pos) return null;
  const a = pos.angle || 0;
  const ca = Math.cos(a), sa = Math.sin(a);
  const ux = ca, uz = -sa;   // width axis (absLeft direction)
  const vx = sa, vz = ca;    // depth axis (into the room)
  const _spE = sidePanelExtents(p, def);
  const _spS = (_spE.right - _spE.left) / 2;
  const hw = (def.width + _spE.left + _spE.right) / 2;
  const hd = def.depth / 2;
  const [minY0, maxY0] = lhcVSpan(p, def);
  return { cx: pos.cx + ux * _spS, cz: pos.cz + uz * _spS, ux, uz, vx, vz, hw, hd,
    minY: minY0 - _spE.bottom, maxY: maxY0 + _spE.top, def, wallId: p.wallId || null };
}

/** lhcVSpan(p, def): vertical [bottom, top] in metres — role base + elevation. (~32999) */
export function lhcVSpan(p, def) {
  if (!def) return [0, 0];
  const e = p.config && p.config.position && p.config.position.elevation ? p.config.position.elevation / 1000 : 0;
  const y0 = roleBaseY(def) + e;
  return [y0, y0 + (def.height || 0)];
}
